import React from 'react';
import {
  Home,
  Inventory2,
  Category,
  PeopleAlt,
  TableChart,
  Code,
  FindInPage,
  ImportExport,
  Public,
  VpnKey,
} from '@mui/icons-material';

import { paths } from '../../../routes/paths';

/**
 * A single item in the admin sidebar. Round 1 only wires "Home" to a
 * real path; the rest carry a `comingSoon` flag so the renderer shows
 * them as disabled with a tooltip. Later rounds flip `comingSoon` off
 * and give each a `path` pointing at its admin sub-route.
 *
 * Shape is intentionally distinct from tenant `SIDEBAR_GROUPS` — tenant
 * sidebar composes groups from route metadata. The admin sidebar is
 * fully declarative because most destinations don't exist yet; wiring
 * them through route metadata now would add empty routes.
 */
export interface AdminSidebarItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ sx?: any }>;
  order: number;
  /** Absolute path; absent when `comingSoon === true`. */
  path?: string;
  comingSoon?: boolean;
}

/**
 * Admin sidebar entries. Mirrors the F12 route tree minus the sub-entity
 * drill-downs — top-level destinations only for Round 1.
 */
export const SIDEBAR_GROUPS_ADMIN: AdminSidebarItem[] = [
  {
    key: 'home',
    label: 'Home',
    icon: Home,
    order: 1,
    path: paths.admin.home(),
  },
  {
    // Wires to the GlobalCatalogLanding picker at /admin/global-catalog.
    // The picker cards each fetch their own row count and link through
    // to the individual List pages (tasks / adventures / cohorts / ...).
    key: 'global-catalog',
    label: 'Global Catalog',
    icon: Inventory2,
    order: 2,
    path: paths.admin.globalCatalog.root(),
  },
  {
    key: 'catalogs',
    label: 'Reference Catalogs',
    icon: Category,
    order: 3,
    path: paths.admin.catalogs.root(),
  },
  {
    key: 'users',
    label: 'Users',
    icon: PeopleAlt,
    order: 4,
    path: paths.admin.users.root(),
  },
  // R7 — four formerly "Coming soon" items flipped to live, each wired
  // to its admin route. Tenant-side equivalents were removed from
  // src/pages/Layout/sidebar/groupsConfig.ts in the same round.
  {
    key: 'field-definitions',
    label: 'Field Definitions',
    icon: TableChart,
    order: 5,
    path: paths.admin.fieldDefinitions(),
  },
  {
    key: 'scripts',
    label: 'Admin Scripts',
    icon: Code,
    order: 6,
    path: paths.admin.scripts(),
  },
  {
    key: 'secret-defaults',
    label: 'Secret Defaults Audit',
    icon: FindInPage,
    order: 7,
    path: paths.admin.secretDefaults(),
  },
  {
    // Per-customer auth_config + secret defaults (super-admin; env-local
    // store selected via the env dropdown — editable on every env).
    key: 'customer-defaults',
    label: 'Customer Defaults',
    icon: VpnKey,
    order: 8,
    path: paths.admin.customerDefaults(),
  },
  {
    // R7 — live. Unified ZIP envelope for admin globals + catalogs.
    key: 'import-export',
    label: 'Import / Export',
    icon: ImportExport,
    order: 9,
    path: paths.admin.importExport(),
  },
  {
    // WAVE G — read-only map of every tenant's authoring home env + drift.
    key: 'editing-env-map',
    label: 'Editing-Env Map',
    icon: Public,
    order: 10,
    path: paths.admin.editingEnvMap(),
  },
];
